import {
  DiffOutlined,
  LockFilled,
  SyncOutlined,
  UnlockOutlined,
  UnorderedListOutlined,
  WarningOutlined,
} from "@ant-design/icons";
import { Alert, Button, Popconfirm, Tag, message } from "antd";
import dayjs from "dayjs";
import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import useMedia from "use-media";
import { Clock } from "~/components/clock/clock.jsx";
import { CloseDay } from "~/components/closeDay/closeDay.jsx";
import { DayChecklistModal } from "~/components/days/DayChecklistModal.jsx";
import { FlightBar } from "~/components/flight/FlightBar.jsx";
import { FlightEditModal } from "~/components/flight/FlightEditModal.jsx";
import { SectionKistStatus } from "~/components/flight/sections/kistStatus.jsx";
import { HelpButton } from "~/components/help/help.jsx";
import { HelpLine } from "~/components/help/helpLine.jsx";
import { MainLayout } from "~/components/layout/layout.jsx";
import { UserModal } from "~/components/profile/userModal.jsx";
import { useWebsocket } from "~/compositions/useWebsocket.js";
import { $t } from "~/i18n.js";
import {
  generate_uuid,
  selectElementByID,
  updateIfNeeded,
} from "~/lib/helpers.js";
import { testDateIsToday, toLocaleDate } from "~/lib/localize.js";
import { selectAlleVliegers } from "~/redux/aanmeldingen/aanmeldingen.reducer.js";
import {
  aanmeldingenActions,
  daysActions,
  flightsActions,
  materiaalActions,
} from "~/redux/actions.js";
import { emptyFlight } from "~/redux/flights/flights.state.js";
import { RemoveDayButton } from "../addDay/removeDayButton.jsx";
import { FlightQueueComponent } from "./flightQueueComponent.jsx";
import { LiveFlightConnection } from "./liveFlightConnection.jsx";
import { helpDocs } from "./start.help.jsx";
import { TabelVluchten } from "./tabel/tabelVluchten.jsx";

export const StartAdministratieComponent = ({
  match,
  history,
  persist,
  flights,
  materiaal,
  days,
  aanmeldingen,
  alleVliegers,
  getAanmeldingen,
  openDay,
  updateStatus,
  newFlight,
  updateFlight,
  getFlights,
  getAllDays,
  getBaseData,
  getDailyInspections,
  saveDay,
}) => {
  const [loading, setLoading] = useState(true);
  const isWide = useMedia({ minWidth: "800px" }, false);

  const [checklistModalType, setChecklistModalType] = useState(null); // 'start' | 'end' | null
  const [checklistReadOnly, setChecklistReadOnly] = useState(false);

  const startChecklistItems = days.activeDay?.checklist_start || [];
  const endChecklistItems = days.activeDay?.checklist_end || [];
  const hasAnyChecklist =
    startChecklistItems.length > 0 || endChecklistItems.length > 0;

  const hasUnansweredRequiredStart = startChecklistItems.some(
    (item) =>
      item.required && (item.value === null || item.value === undefined),
  );

  const inherit_sleep_cat = persist.club?.inherit_sleep_cat;

  // find a sibling flying day on the same date+airport that currently accepts flights
  const siblingAcceptingDay = days.allDays?.find(
    (d) =>
      d.dag_id !== days.activeDay.dag_id &&
      d.datum === days.activeDay.datum &&
      d.vertrek_vliegveld === days.activeDay.vertrek_vliegveld &&
      d.is_vliegend &&
      d.accept_flights,
  );

  // Auto-open start checklist on today's open day if any required items are unanswered
  useEffect(() => {
    if (
      days.activeDay?.status === "open" &&
      days.activeDay?.is_today &&
      hasUnansweredRequiredStart
    ) {
      setChecklistReadOnly(false);
      setChecklistModalType("start");
    }
  }, [days.activeDay?.dag_id, days.activeDay?.status]);

  // Recalculate on every render from the raw datum to avoid stale Redux is_today
  const isToday = testDateIsToday(days.activeDay.datum);

  const canOpenChecklistAudit =
    hasAnyChecklist &&
    (startChecklistItems.length > 0 || endChecklistItems.length > 0);

  // Only connect the WebSocket when the user is an active start admin on today's open day
  const shouldConnect =
    days.activeDay.status === "open" &&
    isToday &&
    persist.profile.wijzig_start &&
    !!persist.club;

  const { connected } = useWebsocket({
    club: persist.club,
    dayId: match.params.id,
    shouldConnect,
    muteSound: false,
  });

  // showFlightbar only on open days, today only, and if permission has to change but without zweefvlieger
  const showFlightBar =
    persist.profile.wijzig_start &&
    isToday &&
    days.activeDay.status === "open" &&
    days.activeDay.accept_flights &&
    persist.profile.group_keys.indexOf("zweefvlieger") === -1 &&
    isWide;

  // current selected flight depends on UUID
  const [activeFlightUUID, setActiveFlightUUID] = useState(null);
  useEffect(() => setActiveFlightUUID(match.params.uuid), [match.params.uuid]);
  const currentFlight = selectElementByID(
    flights.allFlights,
    "uuid",
    activeFlightUUID,
  );

  // Move this logic into a useEffect to avoid calling setState during render
  useEffect(() => {
    if (
      currentFlight &&
      currentFlight.start_methode.endsWith("-a") &&
      currentFlight.sleep_uuid &&
      flights.allFlights.find((f) => f.uuid === currentFlight.sleep_uuid)
    ) {
      setActiveFlightUUID(currentFlight.sleep_uuid);
    }
  }, [currentFlight, flights.allFlights]);

  const currentSleepFlight =
    currentFlight &&
    currentFlight.start_methode === "sleep" &&
    currentFlight.sleep_uuid &&
    selectElementByID(flights.allFlights, "uuid", currentFlight.sleep_uuid);
  const [selectedPilot, setSelectedPilot] = useState(null);

  // only save to queue if the user is allowed to wijzig starts, e.g. combi
  const saveToQueueWhenOffline = persist.profile.wijzig_start;

  // can only add a vlucht if you have the right, or the day is not locked
  // normal user only if day is closed (not locked)
  // admin user (wijzig vluchten) kan do it when day is locked
  const readOnly =
    (days.activeDay.status === "locked" && !persist.profile.wijzig_vluchten) ||
    // startwagen only if day is open, as such when closed it is readonly if wijzig starts
    (days.activeDay.status === "open" && !persist.profile.wijzig_start) ||
    // explicit, only readonly on closed if user is not allowed to fly but update start admin, this is
    (days.activeDay.status !== "open" &&
      !persist.profile.vliegt &&
      persist.profile.wijzig_start) ||
    // launch admin (wijzig_start) is always readonly on days other than today
    (persist.profile.wijzig_start && !persist.profile.wijzig_vluchten && !isToday );

  useEffect(() => {
    updateIfNeeded(
      flights.lastUpdatedBaseData,
      async () => {
        await getBaseData();
      },
      null,
      3600000,
    ); // 1 hour
  }, [flights.lastUpdatedBaseData]);

  useEffect(() => {
    updateIfNeeded(
      materiaal.inspectionsUpdated,
      async () => {
        await getDailyInspections();
      },
      null,
      3600000,
    ); // 1 hour
  }, [materiaal.inspectionsUpdated]);

  useEffect(() => {
    updateIfNeeded(
      days.isPending || days.lastUpdated,
      getAllDays,
      () => days.lastUpdated && openDay(match.params.id),
      3600000,
    ); // 1 hour
  }, [match.params.id]);

  useEffect(() => {
    // only download aanmeldingen if online, day is currently selected, and activeDay is aangemeld
    if (
      days.activeDay.can_aanmeld &&
      days.activeDay.dag_id === parseInt(match.params.id)
    ) {
      // if dag_id is null, then we also have to update
      const trigger = aanmeldingen.dag_id ? aanmeldingen.lastUpdated : null;
      updateIfNeeded(
        trigger,
        () =>
          getAanmeldingen({
            dag_id: match.params.id,
            normalizeAsVliegers: true,
          }),
        null,
        3600000,
      ); // 1 hour
    }
  }, [match.params.id, aanmeldingen.lastUpdated, days.activeDay]);

  const [dayActiveSleepKist, setDayActiveSleepKist] = useState(null);

  useEffect(() => {
    // if only 1 sleep_kist then set this as the activeSleepKist
    if (flights.sleep_kisten && flights.sleep_kisten.length === 1) {
      return setDayActiveSleepKist({
        kist_id: flights.sleep_kisten[0].id,
        registratie: flights.sleep_kisten[0].registratie,
        callsign: flights.sleep_kisten[0].callsign,
        type: flights.sleep_kisten[0].type,
        flarm: flights.sleep_kisten[0].flarm,
        start_methode: flights.sleep_kisten[0].start_methode,
      });
    }

    // 2. Otherwise check if only 1 unique sleepkist is used in flights of that day
    const sleepFlights = flights.allFlights?.filter(
      (f) => f.start_methode?.endsWith("-a") && f.kist_id && !f.is_deleted,
    );

    const uniqueSleepKistIds = [
      ...new Set((sleepFlights || []).map((f) => f.kist_id)),
    ];

    if (uniqueSleepKistIds.length === 1) {
      const firstSleepFlight = sleepFlights[0];
      let start_methode = firstSleepFlight.start_methode.replace("-a", "");
      if (start_methode !== "tmg" && start_methode !== "sep") {
        start_methode = "tmg";
      }
      return setDayActiveSleepKist({
        kist_id: firstSleepFlight.kist_id,
        registratie: firstSleepFlight.registratie,
        callsign: firstSleepFlight.callsign,
        type: firstSleepFlight.type,
        flarm: firstSleepFlight.flarm,
        start_methode,
      });
    }
    return setDayActiveSleepKist(null);
  }, [flights.sleep_kisten, flights.allFlights]);

  const syncData = async () => {
    // hard sync of all data
    await getBaseData();
    await getAllDays();
    await getDailyInspections();
    await getAanmeldingen({
      dag_id: match.params.id,
      normalizeAsVliegers: true,
    });
    await getFlights({ dag_id: match.params.id });
  };

  useEffect(() => {
    // only download flights if online
    const fetchData = async () => {
      const newFlights = await getFlights({
        dag_id: match.params.id,
        skipPending: false,
        origin: "initial_load",
      });
      setLoading(false);

      // open first flight if startwagen, and only open first zweefvlucht
      if (
        showFlightBar &&
        newFlights &&
        newFlights.length > 0 &&
        !match.params.uuid
      ) {
        const filtered =
          newFlights &&
          newFlights.filter(
            (f) => !(f.start_methode.endsWith("-a") && f.sleep_uuid),
          );

        if (filtered && filtered.length > 0)
          setActiveFlightUUID(filtered[0].uuid);
      }
    };
    if (flights.lastUpdatedBaseData && navigator.onLine && !shouldConnect) {
      fetchData();
    } else {
      setLoading(false);
    }
  }, [match.params.id, getFlights, shouldConnect]);

  const createFlight = async ({ extraData }) => {
    // Block flight creation when required checklist items are unanswered
    if (
      hasUnansweredRequiredStart &&
      days.activeDay?.status === "open" &&
      isToday
    ) {
      message.warning(
        $t("Vul eerst de start checklist in voordat je een vlucht toevoegt."),
      );
      setChecklistReadOnly(false);
      setChecklistModalType("start");
      return null;
    }
    // create new flight with optionally extraData, e.g. kist or sleep_uuid
    let category = "";
    if (extraData.start_methode?.endsWith("-a")) {
      category = flights.default_sleep_cat || persist.club.sleep_cat;
    } else if (
      extraData.start_methode === "tmg" ||
      extraData.start_methode === "sep"
    ) {
      category = flights.default_tmg_cat || persist.club.tmg_cat;
    } else {
      category = flights.default_flight_cat || persist.club.flight_cat;
    }
    // extract first category, only if category is not other
    category =
      category.indexOf(",") > 0
        ? (category.split(",")[1] || "").trim()
        : category;

    if (category === "other") category = "";

    const newFlightObject = {
      ...emptyFlight,
      dag_id: days.activeDay.dag_id,
      datum: days.activeDay.datum,

      // in case admin adds a flight, lock it directly
      is_locked: days.activeDay.status === "locked",
      uuid: generate_uuid(),
      index: flights.allFlights.length,
      start_methode:
        extraData?.start_methode || days.activeDay.start_methode || "lier",
      vertrek_vliegveld: days.activeDay.vertrek_vliegveld,
      aankomst_vliegveld: days.activeDay.aankomst_vliegveld,
      category,

      // order is the figure on which it is sorted, see function fights.normalizers.getFlightOrder
      order: 10000 + flights.allFlights.length,

      ...extraData,
    };

    // awaiting and adding sleep uuid if required, and only perform if club has only one sleep kist
    if (
      newFlightObject.start_methode === "sleep" &&
      flights.sleep_kisten.length === 1
    ) {
      newFlightObject.sleep_uuid = generate_uuid();
      await createNewSleepFlight({
        flight: newFlightObject,
        newUUID: newFlightObject.sleep_uuid,
        kist: flights.sleep_kisten[0],
      });
    }

    // Add new and save directly
    await newFlight(newFlightObject);
    await updateFlight(newFlightObject, saveToQueueWhenOffline);

    return newFlightObject.uuid;
  };

  const createNewFlightWithKist = async ({ kist = null }) => {
    let extraData = {
      uuid: generate_uuid(),
    };
    if (kist) {
      extraData = {
        ...extraData,
        kist_id: kist.id,
        registratie: kist.registratie,
        callsign: kist.callsign,
        type: kist.type,
        flarm: kist.flarm,
        start_methode:
          kist.start_methode || days.activeDay.start_methode || "lier",
      };
    }
    await createFlight({ extraData });
    setActiveFlightUUID(extraData.uuid);
  };

  const createNewSleepFlight = async ({ flight, newUUID, kist }) => {
    // Create sleep_flight if not already connected
    // add in extra data to setup the newSleepFlight
    const _currentFlight = flight || currentFlight;

    let extraData = {
      uuid: newUUID || generate_uuid(),
      volg_nummer: null,
      sleep_uuid: _currentFlight.uuid,
      is_deleted: false,
      betalend_lid_id: _currentFlight.betalend_lid_id || "DELETE",
      voucher_id: _currentFlight.voucher_id || "DELETE",

      // add in start/landing times if possible, this is the case that the glider has already been departed (or een landed)
      start_tijd: _currentFlight.start_tijd,
    };

    // inherit possibly category
    if (inherit_sleep_cat) {
      extraData.category = _currentFlight.category;
    }

    // case, current flight is sleep
    if (_currentFlight.start_methode === "sleep") {
      extraData = {
        ...extraData,

        // default fallback to tmg-a, but if there is only 1 sleepkist, use that
        start_methode: "tmg-a",
      };

      // add in sleepVlieger, if on that day there is 1 vlieger that has sleepVlieger duty
      if (
        aanmeldingen.sleepvliegers &&
        aanmeldingen.sleepvliegers.length > 0 &&
        aanmeldingen.sleepvliegers[0]
      ) {
        extraData.gezagvoerder_id = aanmeldingen.sleepvliegers[0].id;
        extraData.gezagvoerder_naam = aanmeldingen.sleepvliegers[0].name;
      }

      // add in sleepKist if active found
      const selectedKist = kist ? kist : dayActiveSleepKist;

      if (selectedKist) {
        extraData.kist_id = selectedKist.id;
        extraData.registratie = selectedKist.registratie;
        extraData.callsign = selectedKist.callsign;
        extraData.type = selectedKist.type;
        extraData.flarm = selectedKist.flarm;
        extraData.start_methode = `${selectedKist.start_methode}-a`;
      }

      // just check if someone has weird data put in here
      if (!["tmg-a", "sep-a"].includes(extraData.start_methode)) {
        extraData.start_methode = "tmg-a";
      }
    } else {
      // case, current flight is not sleep but TMG/SEP, then we create a new sleep flight
      extraData = {
        ...extraData,
        start_methode: "sleep",
      };
    }

    // sleepUUID becomes the one of the newly created flight
    await createFlight({ extraData });
    return extraData.uuid;
  };

  const addNewSleepFlight = async (kist) => {
    // Create sleep_flight if not already connected
    const sleep_uuid = await createNewSleepFlight({ kist });

    // add sleep_uuid to current flight
    await updateFlight({
      uuid: currentFlight.uuid,
      sleep_uuid: sleep_uuid,
    });
  };

  const disconnectSleepFlight = async () => {
    // function to disconnect sleep flight, e.g. when changing kist
    if (currentSleepFlight) {
      await updateFlight({
        uuid: currentSleepFlight.uuid,
        sleep_uuid: "DELETE",
        start_methode: currentSleepFlight.start_methode.replace("-a", ""),
        betalend_lid_id: currentSleepFlight.gezagvoerder_id || "DELETE",
        voucher_id: "DELETE",
      });
    }
    await updateFlight({
      uuid: currentFlight.uuid,
      sleep_uuid: "DELETE",
      start_methode: currentFlight.start_methode.replace("-a", ""),
    });
  };

  const connectSleepFlight = async ({ sleep_uuid, sleep_start_methode }) => {
    // function to connect sleep and update the sleep flight's start methode to match this flight

    await updateFlight({
      uuid: currentFlight.uuid,
      sleep_uuid: sleep_uuid,
      is_deleted: false,
    });

    let flightData = {
      uuid: sleep_uuid,
      sleep_uuid: currentFlight.uuid,
      betalend_lid_id: currentFlight.betalend_lid_id,
      voucher_id: currentFlight.voucher_id || "DELETE",
      start_methode: sleep_start_methode,
      is_deleted: false,
    };

    if (inherit_sleep_cat) {
      flightData.category = currentFlight.category;
    }

    await updateFlight(flightData);
  };

  const triggerUpdateFlight = async (data) => {
    // 0 auto-create tow flight when switching an existing flight to "sleep" with exactly 1 towplane
    if (
      data.start_methode === "sleep" &&
      flights.sleep_kisten.length === 1 &&
      !data.sleep_uuid
    ) {
      const existingFlight = selectElementByID(
        flights.allFlights,
        "uuid",
        data.uuid,
      );
      if (
        existingFlight &&
        existingFlight.start_methode !== "sleep" &&
        !existingFlight.sleep_uuid
      ) {
        const newSleepUUID = generate_uuid();
        data.sleep_uuid = newSleepUUID;
        await createNewSleepFlight({
          flight: { ...existingFlight, ...data },
          newUUID: newSleepUUID,
          kist: flights.sleep_kisten[0],
        });
      }
    }

    // 1 register actions landing en/of start
    if (data.start_tijd && data.landings_tijd) {
      data.vluchtduur = dayjs(data.landings_tijd, "HH:mm").diff(
        dayjs(data.start_tijd, "HH:mm"),
        "minute",
      );
      // correct negative
      if (data.vluchtduur < 0) {
        data.vluchtduur = 0;
        message.error(
          `Kist kan niet starten nadat het geland is, landingstijd aangepast naar ${data.start_tijd}`,
        );
        data.landings_tijd = data.start_tijd;
      }
      // for TMG correct blocktimes, by default we take 10 minutes additional to flighttime
      if (data.start_methode?.startsWith("tmg")) {
        data.blocktime = data.vluchtduur + 10;
      }
    }

    // 2 extract the volgnummer in case it is none
    if (
      !data.start_methode?.endsWith("-a") &&
      data.start_tijd &&
      !data.volg_nummer
    ) {
      // get last volg_nummer of all Flights
      const volg_nummer = flights.allFlights.reduce((max, flight) => {
        if (flight.volg_nummer && flight.volg_nummer > max)
          return flight.volg_nummer;
        return max;
      }, 0);
      data.volg_nummer = volg_nummer + 1;
    }

    // 3 save data to redux
    const result = updateFlight(data, saveToQueueWhenOffline);
    return result;
  };

  const docs = helpDocs();
  return (
    <MainLayout
      history={history}
      renderHeadless={persist.profile.wijzig_start}
      isLoading={
        flights.isPending ||
        days.isPending ||
        loading ||
        !days.activeDay.can_start
      }
    >
      <DayChecklistModal
        visible={!!checklistModalType}
        type={checklistModalType || "start"}
        items={
          checklistModalType === "end" ? endChecklistItems : startChecklistItems
        }
        startItems={startChecklistItems}
        endItems={endChecklistItems}
        dagId={days.activeDay.dag_id}
        onClose={() => setChecklistModalType(null)}
        onComplete={() => setChecklistModalType(null)}
        readOnly={checklistReadOnly}
      />

      <UserModal
        visible={selectedPilot && selectedPilot.id}
        onClose={() => setSelectedPilot(null)}
        pilot={selectedPilot}
        currentUser={persist.profile}
        newNotities={[]}
        link={null}
      />

      <div
        className="row"
        style={{
          justifyContent: "space-between",
          flexWrap: "wrap",
          alignItems: "center",
          marginRight: 55,
        }}
      >
        <p className="large">
          {isWide && <Clock />}
          {days.activeDay.status === "locked" && (
            <LockFilled style={{ color: "black", marginRight: 4 }} />
          )}
          {days.activeDay.status === "closed" && (
            <UnlockOutlined style={{ color: "black", marginRight: 4 }} />
          )}
          <span>
            {toLocaleDate(new Date(days.activeDay.datum))} -{" "}
            {days.activeDay.type_dag}
          </span>
        </p>

        <div
          className="row"
          style={{
            alignItems: "center",
            gap: 6,
            marginTop: 6,
            marginRight: 12,
          }}
        >
          {!readOnly &&
          isToday &&
          persist.profile.wijzig_start &&
          days.activeDay.status === "open" &&
          days.activeDay.accept_flights &&
          persist.club ? (
            <>
              <FlightQueueComponent syncData={syncData} />
              <LiveFlightConnection
                club={persist.club}
                dayId={match.params.id}
                connected={connected}
              />
            </>
          ) : (
            <Button
              loading={flights.isPending}
              size="small"
              onClick={() => syncData()}
              icon={<SyncOutlined />}
            >
              {$t("Sync")}
            </Button>
          )}

          {!readOnly &&
            (persist.profile?.ziet_staff ||
              persist.profile?.wijzig_rooster) && (
              <RemoveDayButton
                size="small"
                disabled={flights.allFlights.length > 0}
                dag_id={days.activeDay.dag_id}
                history={history}
              />
            )}

          {days.activeDay.status !== "open" && (
            <Popconfirm
              placement="bottom"
              title={
                <p>
                  {$t("Zeker weten dat je een nieuwe vlucht wilt toevoegen?")}
                  <br />
                  {$t(
                    "De vlucht is direct zichtbaar in de startadministratie.",
                  )}
                </p>
              }
              onConfirm={() => {
                // sometimes you can create a new flight but actually already one is presented for you, as such use this one
                if (
                  flights.allFlights &&
                  flights.allFlights.length > 0 &&
                  !flights.allFlights[0].registratie &&
                  !flights.allFlights[0].start_methode.endsWith("-a")
                ) {
                  return setActiveFlightUUID(flights.allFlights[0].uuid);
                }
                return createNewFlightWithKist({ kist: null });
              }}
              disabled={readOnly}
              okText={$t("Ja")}
              cancelText={$t("Nee")}
            >
              <Button size="small" disabled={readOnly}>
                {$t("Voeg vlucht toe")}
              </Button>
            </Popconfirm>
          )}

          <Button
            size="small"
            disabled={days.activeDay.status === "open"}
            icon={<DiffOutlined />}
            href={"/total/" + match.params.id}
          >
            {$t("Dagtotaal")}
          </Button>

          {canOpenChecklistAudit && (
            <Button
              size="small"
              icon={<UnorderedListOutlined />}
              onClick={() => {
                setChecklistReadOnly(true);
                setChecklistModalType("start");
              }}
            />
          )}

          <CloseDay
            dag_id={match.params.id}
            canClose={days.activeDay.status === "open"}
            canOpen={
              days.activeDay.status === "closed" &&
              testDateIsToday(days.activeDay.datum)
            }
            updateStatus={updateStatus}
            getFlights={getFlights}
            activeDay={days.activeDay}
          />

          <HelpButton
            title={$t("Start administratie")}
            subtitle={$t("Voer hier de start administratie van de dag door.")}
            helpDocs={docs}
          />
        </div>
      </div>

      {hasUnansweredRequiredStart &&
        days.activeDay.status === "open" &&
        isToday && (
          <Alert
            type="warning"
            showIcon
            icon={<WarningOutlined />}
            style={{ marginBottom: 8 }}
            message={$t("Checklist incompleet")}
            description={$t(
              "Er zijn blokkerende items die nog niet zijn ingevuld. Vluchten kunnen hierdoor niet worden toegevoegd.",
            )}
            action={
              <Button
                size="small"
                disabled={
                  !(
                    persist.profile.wijzig_dagen || persist.profile.wijzig_start
                  )
                }
                onClick={() => {
                  setChecklistReadOnly(false);
                  setChecklistModalType("start");
                }}
              >
                {$t("Invullen")}
              </Button>
            }
          />
        )}

      {days.activeDay.status !== "open" && (
        <div className="row" style={{ marginBottom: 6 }}>
          <Tag color="red" style={{ marginRight: 4 }}>
            {$t("Gesloten")}
          </Tag>
          {days.activeDay.status === "closed" ? (
            <p>{$t("Correcties mogelijk")}</p>
          ) : (
            <p>{$t("Correcties niet meer mogelijk")}</p>
          )}
        </div>
      )}

      {days.activeDay.status === "open" && !persist.profile.wijzig_start && (
        <div className="row" style={{ marginBottom: 6 }}>
          <Tag color="green" style={{ marginRight: 4 }}>
            {$t("Open")}
          </Tag>
          <p>{$t("Tijdschrijven kan nu alleen vanuit de startwagen.")}</p>
        </div>
      )}

      {showFlightBar && (
        <div className="row">
          <SectionKistStatus
            is_today={isToday}
            createNewFlightWithKist={createNewFlightWithKist}
            openFlightUUID={setActiveFlightUUID}
            activeFlightKistID={currentFlight?.kist_id}
            updateFlight={triggerUpdateFlight}
          />
        </div>
      )}

      {currentFlight && (
        <>
          {showFlightBar ? (
            <div
              style={{
                position: "sticky",
                top: 0,
                zIndex: 100,
                background: "#fff",
              }}
            >
              <FlightBar
                club={persist.club}
                readOnlyDay={readOnly}
                isPending={flights.isPending}
                flight={currentFlight}
                allFlights={flights.allFlights}
                updateFlight={triggerUpdateFlight}
                sleepFlight={currentSleepFlight}
                disconnectSleepFlight={disconnectSleepFlight}
                connectSleepFlight={connectSleepFlight}
                addNewSleepFlight={addNewSleepFlight}
                kisten={flights.kisten}
                sleep_kisten={flights.sleep_kisten}
                vliegers={alleVliegers}
                aanmeldingen={
                  aanmeldingen.dag_id === match.params.id
                    ? aanmeldingen.aanmeldingen
                    : []
                }
                activeDay={days.activeDay}
                setSelectedPilot={setSelectedPilot}
                enable_authorization={persist?.club?.enable_authorization}
                showFinance={persist?.club?.plan !== "small"}
                dayActiveSleepKist={dayActiveSleepKist}
              />
            </div>
          ) : (
            <FlightEditModal
              club={persist.club}
              allFlights={flights.allFlights}
              disconnectSleepFlight={disconnectSleepFlight}
              connectSleepFlight={connectSleepFlight}
              addNewSleepFlight={addNewSleepFlight}
              readOnly={readOnly}
              currentUser={persist.profile}
              visible={!!setActiveFlightUUID}
              onClose={() => {
                history.replace(`/start/${match.params.id}`);
                setActiveFlightUUID(null);
              }}
              flight={currentFlight}
              updateFlight={(data) => triggerUpdateFlight({ ...data })}
              sleepFlight={currentSleepFlight}
              kisten={flights.kisten}
              sleep_kisten={flights.sleep_kisten}
              vliegers={alleVliegers}
              aanmeldingen={
                aanmeldingen.dag_id === match.params.id
                  ? aanmeldingen.aanmeldingen
                  : []
              }
              setSelectedPilot={setSelectedPilot}
            />
          )}
        </>
      )}

      {days.activeDay.gaat_door &&
        !days.activeDay.accept_flights &&
        days.activeDay.status === "open" &&
        (persist.profile?.wijzig_dagen ||
          persist.profile?.wijzig_start ||
          persist.profile?.wijzig_rooster) && (
          <Alert
            type="warning"
            showIcon
            style={{ marginBottom: 8 }}
            message={$t("Dag accepteert geen vluchten")}
            description={
              <div
                className="column"
                style={{ gap: 8, alignItems: "flex-start" }}
              >
                {siblingAcceptingDay && (
                  <p>
                    {$t("Vluchten worden geregistreerd op")}{" "}
                    <strong>{siblingAcceptingDay.type_dag}</strong>:{" "}
                    {siblingAcceptingDay.titel}
                  </p>
                )}

                <HelpLine>
                  {$t(
                    "Er zijn meerdere dagen op dezelfde datum en vliegveld, om te voorkomen dat vluchten per ongeluk op de verkeerde dag worden geregistreerd, is deze dag geblokkeerd voor het accepteren van vluchten.",
                  )}
                </HelpLine>

                {siblingAcceptingDay && (
                  <Button
                    size="small"
                    onClick={() =>
                      history.push(`/start/${siblingAcceptingDay.dag_id}`)
                    }
                  >
                    {$t("Navigeer naar actieve dag")}
                  </Button>
                )}

                <Button
                  size="small"
                  type="primary"
                  onClick={() =>
                    saveDay({
                      dag_id: days.activeDay.dag_id,
                      accept_flights: true,
                    })
                  }
                >
                  {$t("Activeer deze dag")}
                </Button>
              </div>
            }
          />
        )}

      <div className="row" style={{ alignItems: "flex-start" }}>
        <TabelVluchten
          showLandingButton={showFlightBar}
          renderFullWidth={
            (!isWide && !persist.profile.wijzig_start) ||
            persist.profile.wijzig_start
          }
          getPending={flights.isPending || loading}
          allFlights={flights.allFlights}
          activeFlightUUID={activeFlightUUID}
          openFlight={(uuid) =>
            history.replace(`/start/${match.params.id}/${uuid}`)
          }
          updateFlight={triggerUpdateFlight}
        />
      </div>
    </MainLayout>
  );
};

const mapStateToProps = (state) => ({
  aanmeldingen: state.aanmeldingen,
  persist: state.persist,
  flights: state.flights,
  days: state.days,
  materiaal: state.materiaal,
  alleVliegers: selectAlleVliegers(state),
});

const mapDispatchToProps = (dispatch) => {
  return {
    getFlights: (d) => dispatch(flightsActions.getFlights(d)),
    newFlight: (d) => dispatch(flightsActions.newFlight(d)),
    updateFlight: (d, saveToQueueWhenOffline) =>
      dispatch(flightsActions.updateFlight(d, saveToQueueWhenOffline)),
    updateStatus: (d) => dispatch(daysActions.updateStatus(d)),
    openDay: (d) => dispatch(daysActions.openDay(d)),
    getAanmeldingen: (d) => dispatch(aanmeldingenActions.getAanmeldingen(d)),
    getAllDays: () => dispatch(daysActions.getAllDays()),
    getBaseData: () => dispatch(flightsActions.getBaseData()),
    getDailyInspections: () => dispatch(materiaalActions.getDailyInspections()),
    saveDay: (d) => dispatch(daysActions.saveDay(d)),
    updateChecklist: (d) => dispatch(daysActions.updateChecklist(d)),
  };
};

export const StartAdministratiePagina = connect(
  mapStateToProps,
  mapDispatchToProps,
)(StartAdministratieComponent);
